import { fetchWithAuth } from '../../utils/api';
import { API_URL } from '../../utils';

// ---------------------------------------------------------------------------
// Types (mirror backend ProgramService interfaces)
// ---------------------------------------------------------------------------

export interface TrainingProgram {
  id: number;
  name: string;
  description: string | null;
  type: string;
  frequency: number;
  duration_weeks: number | null;
  created_at: string;
}

export interface ProgramExercise {
  id: number;
  program_id: number;
  name: string;
  muscle_group: string | null;
  equipment: string | null;
  increment_kg: number;
  notes: string | null;
}

export interface TemplateExercise {
  id: number;
  template_id: number;
  program_exercise_id: number;
  program_exercise_name: string;
  sets: number;
  reps: number;
  order_in_template: number;
  notes: string | null;
}

export interface WorkoutTemplate {
  id: number;
  program_id: number;
  name: string;
  order_index: number;
  exercises: TemplateExercise[];
}

export interface ProgramDetail extends TrainingProgram {
  templates: WorkoutTemplate[];
}

export interface ExerciseState {
  id: number;
  enrollment_id: number;
  program_exercise_id: number;
  program_exercise_name: string;
  current_weight_kg: number;
  consecutive_failures: number;
  user_exercise_id: number;
  updated_at: string;
}

export interface Enrollment {
  id: number;
  user_id: number;
  program_id: number;
  program_name: string;
  status: 'active' | 'paused' | 'completed';
  next_template_index: number;
  sessions_completed: number;
  enrolled_at: string;
  updated_at: string;
  exercise_states: ExerciseState[];
}

export interface NextWorkoutExercise {
  program_exercise_id: number;
  name: string;
  sets: number;
  reps: number;
  current_weight_kg: number;
  consecutive_failures: number;
  order_in_template: number;
  notes: string | null;
}

export interface NextWorkout {
  template_id: number;
  template_name: string;
  exercises: NextWorkoutExercise[];
}

export interface ExerciseResult {
  program_exercise_id: number;
  sets_completed: number;
  reps_completed: number[];
  weight_used_kg: number;
}

export interface SessionLogInput {
  date: string;
  template_id: number;
  notes?: string;
  exercise_results: ExerciseResult[];
}

export interface ProgramSession {
  id: number;
  enrollment_id: number;
  template_id: number;
  template_name: string;
  workout_id: number;
  completed_at: string;
  session_date: string;
  notes: string | null;
}

export interface StartingWeight {
  program_exercise_id: number;
  weight_kg: number;
}

export interface ProgressionPoint {
  completed_at: string;
  weight_kg: number;
}

export interface ProgressionHistory {
  program_exercise_id: number;
  exercise_name: string;
  history: ProgressionPoint[];
}

export interface SessionResult {
  exercise_name: string;
  sets_completed: number;
  reps_completed: number[];
  weight_used_kg: number;
  succeeded: boolean;
}

export interface ProgramSessionForDate {
  session_id: number;
  program_name: string;
  template_name: string;
  completed_at: string;
  notes: string | null;
  results: SessionResult[];
}

export interface LastSessionExercise {
  program_exercise_id: number;
  exercise_name: string;
  sets: number;
  reps: number;
  weight_used_kg: number;
  succeeded: boolean;
}

export interface LastSessionDetails {
  session_id: number;
  template_id: number;
  template_name: string;
  completed_at: string;
  session_date: string;
  notes: string | null;
  exercises: LastSessionExercise[];
}

export interface SessionEditInput {
  notes?: string;
  results: Array<{ program_exercise_id: number; succeeded: boolean }>;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export class ProgramApiService {
  // 6.1 — List all available training programs
  static async getPrograms(): Promise<TrainingProgram[]> {
    const response = await fetchWithAuth(`${API_URL}/programs`);
    if (!response.ok) throw new Error(`Failed to fetch programs: ${response.status}`);
    return response.json();
  }

  // 6.2 — Get full program details (templates + exercises)
  static async getProgramDetails(programId: number): Promise<ProgramDetail> {
    const response = await fetchWithAuth(`${API_URL}/programs/${programId}`);
    if (!response.ok) throw new Error(`Failed to fetch program: ${response.status}`);
    return response.json();
  }

  // 6.3 — Get all enrollments for the current user
  static async getEnrollments(): Promise<Enrollment[]> {
    const response = await fetchWithAuth(`${API_URL}/programs/enrollments`);
    if (!response.ok) throw new Error(`Failed to fetch enrollments: ${response.status}`);
    return response.json();
  }

  // 6.4 — Enroll in a program with optional starting weights and starting template
  static async enroll(
    programId: number,
    startingWeights: StartingWeight[] = [],
    startingTemplateIndex: number = 0
  ): Promise<Enrollment> {
    const response = await fetchWithAuth(`${API_URL}/programs/${programId}/enroll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        starting_weights: startingWeights,
        starting_template_index: startingTemplateIndex,
      }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw Object.assign(
        new Error(body.error ?? `Failed to enroll: ${response.status}`),
        { status: response.status }
      );
    }
    return response.json();
  }

  // 6.5 — Update enrollment status (pause or resume)
  static async updateEnrollment(
    enrollmentId: number,
    status: 'active' | 'paused'
  ): Promise<Enrollment> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/enrollments/${enrollmentId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error ?? `Failed to update enrollment: ${response.status}`);
    }
    return response.json();
  }

  // 6.6 — Leave (delete) an enrollment
  static async leaveProgram(enrollmentId: number): Promise<void> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/enrollments/${enrollmentId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error ?? `Failed to leave program: ${response.status}`);
    }
  }

  // 6.7 — Get the next workout suggestion for an enrollment
  static async getNextWorkout(enrollmentId: number): Promise<NextWorkout> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/enrollments/${enrollmentId}/next`
    );
    if (!response.ok) throw new Error(`Failed to fetch next workout: ${response.status}`);
    return response.json();
  }

  // 6.8 — Log a completed session
  static async logSession(
    enrollmentId: number,
    payload: SessionLogInput
  ): Promise<ProgramSession> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/enrollments/${enrollmentId}/sessions`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw Object.assign(
        new Error(body.error ?? `Failed to log session: ${response.status}`),
        { status: response.status }
      );
    }
    return response.json();
  }

  // 6.9 — Get session history for an enrollment
  static async getSessionHistory(enrollmentId: number): Promise<ProgramSession[]> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/enrollments/${enrollmentId}/sessions`
    );
    if (!response.ok) throw new Error(`Failed to fetch session history: ${response.status}`);
    return response.json();
  }

  // 6.10 — Get weight progression history per exercise
  static async getProgressionHistory(enrollmentId: number): Promise<ProgressionHistory[]> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/enrollments/${enrollmentId}/progress`
    );
    if (!response.ok) throw new Error(`Failed to fetch progress: ${response.status}`);
    return response.json();
  }

  // 6.11 — Get distinct dates with program sessions for the given month (YYYY-MM)
  static async getProgramSessionDates(month: string): Promise<string[]> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/sessions/dates?month=${month}`
    );
    if (!response.ok) throw new Error(`Failed to fetch program session dates: ${response.status}`);
    return response.json();
  }

  // 6.12 — Get all program sessions for a specific date (YYYY-MM-DD)
  static async getProgramSessionsForDate(date: string): Promise<ProgramSessionForDate[]> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/sessions/by-date?date=${date}`
    );
    if (!response.ok) throw new Error(`Failed to fetch program sessions: ${response.status}`);
    return response.json();
  }

  // 6.13 — Get the last session for an enrollment (with prescribed sets/reps)
  static async getLastSession(enrollmentId: number): Promise<LastSessionDetails | null> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/enrollments/${enrollmentId}/sessions/last`
    );
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Failed to fetch last session: ${response.status}`);
    return response.json();
  }

  // 6.14 — Delete the last session (rolls back progression)
  static async deleteLastSession(enrollmentId: number): Promise<void> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/enrollments/${enrollmentId}/sessions/last`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw Object.assign(
        new Error((body as any).error ?? `Failed to delete last session: ${response.status}`),
        { status: response.status }
      );
    }
  }

  // 6.15 — Edit the last session (re-applies progression with new success/fail)
  static async editLastSession(enrollmentId: number, payload: SessionEditInput): Promise<ProgramSession> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/enrollments/${enrollmentId}/sessions/last`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw Object.assign(
        new Error((body as any).error ?? `Failed to edit last session: ${response.status}`),
        { status: response.status }
      );
    }
    return response.json();
  }

  // 6.16 — Update starting weights (only before first session)
  static async updateEnrollmentWeights(
    enrollmentId: number,
    weights: StartingWeight[]
  ): Promise<void> {
    const response = await fetchWithAuth(
      `${API_URL}/programs/enrollments/${enrollmentId}/weights`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weights }),
      }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw Object.assign(
        new Error((body as any).error ?? `Failed to update weights: ${response.status}`),
        { status: response.status }
      );
    }
  }
}
